import { createTheme } from "@mui/material";
import { useBrandingContext } from "./BrandingContext";
import GetContrastColor from "./utils/helpers/GetContrastColor";
import ExtractSubdomain from "./utils/helpers/ExtractSubdomain";
import { createButtonVariants } from "./theme/buttonVariants";
import { buttonPalette } from "./theme/colorPalette";
import { layoutWidths } from "./theme/layoutWidths";

const useCustomTheme = () => {
  const { loading, branding } = useBrandingContext();

  const primaryContrast = GetContrastColor(branding?.primaryColor ?? null);
  const secondaryContrast = GetContrastColor(branding?.secondaryColor ?? null);

  let primaryMain = "#FFFFFF";
  let primaryHover = "#FFFFFF";
  let primaryOpposite = "#FFFFFF";
  let primaryOppositeHover = "#FFFFFF";
  let secondaryMain = "#FFFFFF";
  let secondaryHover = "#FFFFFF";
  let tertiaryMain = "#FFFFFF";
  let backgroundMain = "#FFFFFF";
  let backgroundHover = "#FFFFFF";
  let accent1 = "#EFEFEF";
  let accent2 = "#A5A5A5";
  let errorMain = "#FF0000";
  let errorContrast = "#FFFFFF";
  let primaryGreen = "#61AF2E";

  const subdomain = ExtractSubdomain(window.location.hostname);

  switch (subdomain) {
    case "portal":
    case "":
      primaryMain = "#000F59"; // "#100E0D" old black
      primaryHover = "rgba(0, 15, 89, 0.75)"; // "rgba(16, 14, 13, 0.75)" old black
      secondaryMain = "#0022CC";
      secondaryHover = "rgba(1, 34, 204, 0.75)";
      tertiaryMain = "#fc9006";
      backgroundMain = "#EEF1F7";
      backgroundHover = "rgba(238, 241, 247, 0.75)";
      break;
    default:
      if (branding) {
        primaryMain = `rgb(${branding?.primaryColor?.join(",")})`; //"#000F59"; // "#100E0D" old black
        primaryHover = `rgba(${branding?.primaryColor?.join(",")}, 0.75)`; //"rgba(0, 15, 89, 0.75)"; // "rgba(16, 14, 13, 0.75)" old black
        secondaryMain = `rgb(${branding?.secondaryColor?.join(",")})`; //"#FFB806";
        secondaryHover = `rgba(${branding?.secondaryColor?.join(",")}, 0.75)`; //"rgba(255, 184, 6, 0.75)";
        tertiaryMain =
          branding?.tertiaryColor?.length > 0
            ? `rgb(${branding?.tertiaryColor?.join(",")})`
            : "#fc9006";
        backgroundMain =
          branding?.backgroundColor?.length > 0
            ? `rgb(${branding?.backgroundColor?.join(",")})`
            : "#EEF1F7";
        backgroundHover =
          branding?.backgroundColor?.length > 0
            ? `rgba(${branding?.backgroundColor?.join(",")}, 0.75)`
            : "rgba(238, 241, 247, 0.75)";
      } else if (!branding && !loading) {
        primaryMain = "#000F59"; // "#100E0D" old black
        primaryHover = "rgba(0, 15, 89, 0.75)"; // "rgba(16, 14, 13, 0.75)" old black
        secondaryMain = "#0022CC";
        secondaryHover = "rgba(1, 34, 204, 0.75)";
        tertiaryMain = "#fc9006";
        backgroundMain = "#EEF1F7";
        backgroundHover = "rgba(238, 241, 247, 0.75)";
      }
      break;
  }

  // Remap colors if brand colors are too light
  const primaryMainIsFlipped = primaryContrast === "#000000";
  const palettePrimaryMain = primaryMainIsFlipped ? "#000000" : primaryMain;
  const palettePrimaryContrast = primaryMainIsFlipped ? "#FFFFFF" : primaryContrast;

  // Feed the live branding-derived colors
  const dynamicButtonVariants = createButtonVariants({
    primary:   { c: primaryMain,   fg: primaryContrast },
    secondary: { c: secondaryMain, fg: secondaryContrast },
    tertiary:  buttonPalette.tertiary,
  });

  return createTheme({
    layout: layoutWidths,
    typography: {
      fontFamily: "Figtree", // Apply the Figtree font to all text in the theme
    },
    components: {
      // POTENTIAL FUTURE UPDATES
      // MuiCard: {
      //   styleOverrides: {
      //     root: {
      //       border: "1px solid lightgrey",
      //       boxShadow: "none",
      //       borderRadius: "10px",
      //     },
      //   },
      // },
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: backgroundMain,
            border: "none",
            boxShadow: "none"
          }
        },
        variants: [
          {
            props: { variant: "borderlessHover" },
            style: {
              border: "none",
              boxShadow: "none",
              "&.Mui-expanded": {
                margin: "0",
              },
              "&.Mui-expanded:before": {
                opacity: "1 !important",
              },
            },
          },
        ],
      },
      MuiAccordionSummary: {
        variants: [
          {
            props: { variant: "borderlessHover" },
            style: {
              pb: 1,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
                cursor: "pointer",
              },
            },
          },
        ],
      },
      MuiBox: {
        styleOverrides: {
          root: {
            backgroundColor: backgroundMain,
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            // all links default to primary or black
            fontFamily: "Figtree",
            color: primaryContrast === "#000000" ? "#000000" : primaryMain,
          },
        },
        variants: [
          {
            props: { variant: "link" },
            style: {
              color: "black",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "linkContrast" },
            style: {
              color: "white",
              fontFamily: "Figtree",
            },
          },
        ],
      },
      MuiTypography: {
        variants: [
          {
            props: { variant: "h1" },
            style: {
              color: "black",
              fontWeight: 600,
              fontSize: "32px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "h1Contrast" },
            style: {
              color: primaryContrast,
              fontWeight: 600,
              fontSize: "32px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "h2" },
            style: {
              color: "black",
              fontWeight: 600,
              fontSize: "28px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "h2Contrast" },
            style: {
              color: primaryContrast,
              fontWeight: 600,
              fontSize: "28px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "h3" },
            style: {
              color: "black",
              fontWeight: 500,
              fontSize: "26px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "h3Contrast" },
            style: {
              color: primaryContrast,
              fontWeight: 500,
              fontSize: "26px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "h4" },
            style: {
              color: "black",
              fontWeight: 500,
              fontSize: "22px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "h4Contrast" },
            style: {
              color: primaryContrast,
              fontWeight: 500,
              fontSize: "22px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "h5" },
            style: {
              color: "black",
              fontWeight: 500,
              fontSize: "20px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "h5Contrast" },
            style: {
              color: primaryContrast,
              fontWeight: 500,
              fontSize: "20px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "h6" },
            style: {
              color: "black",
              fontWeight: 500,
              fontSize: "18px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "h6Contrast" },
            style: {
              color: primaryContrast,
              fontWeight: 500,
              fontSize: "18px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "subtitle1" },
            style: {
              color: "black",
              fontWeight: 400,
              fontSize: "16px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "subtitle1Contrast" },
            style: {
              color: primaryContrast,
              fontWeight: 400,
              fontSize: "16px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "subtitle2" },
            style: {
              color: "black",
              fontWeight: 500,
              fontSize: "14px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "subtitle2Contrast" },
            style: {
              color: primaryContrast,
              fontWeight: 500,
              fontSize: "14px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "body1" },
            style: {
              color: "black",
              fontWeight: 600,
              fontSize: "16px",
              fontFamily: "Figtree",
              lineHeight: "22px"
            },
          },
          {
            props: { variant: "body1Contrast" },
            style: {
              color: primaryContrast === "#000000" ? "#000000" : primaryMain,
              fontWeight: 600,
              fontSize: "16px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "body2" },
            style: {
              color: "black",
              fontWeight: 400,
              fontSize: "16px",
              fontFamily: "Figtree",
              lineHeight: "22px"
            },
          },
          {
            props: { variant: "body2Contrast" },
            style: {
              color: primaryContrast,
              fontWeight: 400,
              fontSize: "16px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "body3" },
            style: {
              color: "black",
              fontWeight: 400,
              fontSize: "14px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "body3Contrast" },
            style: {
              color: primaryContrast,
              fontWeight: 400,
              fontSize: "14px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "caption" },
            style: {
              color: "black",
              fontWeight: 400,
              fontSize: "12px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "captionContrast" },
            style: {
              color: primaryContrast,
              fontWeight: 400,
              fontSize: "12px",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "overline" },
            style: {
              color: "black",
              fontWeight: 700,
              fontSize: "14px",
              lineHeight: "24px",
              textTransform: "uppercase",
              fontFamily: "Figtree",
            },
          },
          {
            props: { variant: "overlineContrast" },
            style: {
              color: primaryContrast,
              fontWeight: 700,
              fontSize: "14px",
              lineHeight: "24px",
              textTransform: "uppercase",
              fontFamily: "Figtree",
            },
          },
        ],
      },
      MuiButton: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            ...(ownerState.color === "primary" &&
              ownerState.variant !== "outlinedContrast" && {
              color: primaryContrast === "#000000" ? "#000000" : primaryMain,
            }),
          }),
        },
        variants: [
          {
            props: { variant: "Button1" },
            style: {
              backgroundColor: primaryMain,
              color: primaryContrast,
              textTransform: "none",
              fontSize: "16px",
              height: "44px",
              borderRadius: "5px",
              border: '2px solid',
              borderColor: primaryContrast === "#000000" ? "#000000" : primaryMain,
              ":hover": {
                backgroundColor: "white",
                color: primaryContrast === "#000000" ? "#000000" : primaryMain,
                border: '2px solid',
                borderColor: primaryContrast === "#000000" ? "#000000" : primaryMain,
                fontWeight: 600
              },
              ":disabled": {
                color: primaryContrast,
              },
            },
          },
          {
            props: { variant: "Button1", small: true },
            style: {
              fontSize: "14px",
              height: "36px",
              padding: "4px 12px",
            },
          },
          {
            props: { variant: "Button1", inverted: true },
            style: {
              backgroundColor: "white",
              color: primaryMain,
              border: '1px solid',
              borderColor: primaryMain,
              ":hover": {
                backgroundColor: primaryMain,
                color: "white",
                border: '1px solid',
                borderColor: primaryMain,
                fontWeight: 400
              },
            },
          },
          {
            props: { variant: "Button1", inverted: true, small: true },
            style: {
              fontSize: "14px",
              height: "36px",
              padding: "4px 12px",
            },
          },
          {
            props: { variant: "Button2" },
            style: {
              backgroundColor: secondaryMain,
              color: secondaryContrast,
              textTransform: "none",
              fontSize: "16px",
              height: "44px",
              borderRadius: "5px",
              border: '2px solid',
              borderColor: secondaryContrast === "#000000" ? "#000000" : secondaryMain,
              ":hover": {
                backgroundColor: "white",
                color: secondaryContrast === "#000000" ? "#000000" : secondaryMain,
                border: '2px solid',
                borderColor: secondaryContrast === "#000000" ? "#000000" : secondaryMain,
                fontWeight: 600
              },
              ":disabled": {
                color: secondaryContrast,
              },
            },
          },
          {
            props: { variant: "Button2", small: true },
            style: {
              fontSize: "14px",
              height: "36px",
              padding: "4px 12px",
            },
          },
          {
            props: { variant: "ButtonError" },
            style: {
              backgroundColor: errorMain,
              color: errorContrast,
              border: '2px solid',
              borderColor: errorMain,
              fontSize: "14px",
              height: "36px",
              padding: "4px 12px",
              ":hover": {
                backgroundColor: "white",
                color: errorMain,
                border: '2px solid',
                borderColor: errorMain,
                fontWeight: 600
              },
              ":disabled": {
                color: errorContrast,
              },
            },
          },
          {
            props: { variant: "ButtonError", inverted: true },
            style: {
              backgroundColor: errorContrast,
              color: errorMain,
              border: '1px solid',
              borderColor: errorMain,
              ":hover": {
                backgroundColor: errorMain,
                color: errorContrast,
                border: '1px solid',
                borderColor: errorMain,
                fontWeight: 400
              },
              ":disabled": {
                color: errorMain,
              },
            },
          },
          ...dynamicButtonVariants,
          // Outlined CTA for use on primary.main surfaces (e.g. assume-view banner).
          // outlinedPrimary uses primary color for stroke/text and is invisible on primary.main.
          {
            props: { variant: "outlinedContrast" },
            style: {
              fontFamily: "Figtree",
              fontWeight: 500,
              lineHeight: 1,
              borderRadius: "999px",
              textTransform: "none",
              whiteSpace: "nowrap",
              fontSize: "16px",
              padding: "0 22px",
              height: "44px",
              backgroundColor: "transparent",
              color: palettePrimaryContrast,
              border: "1.5px solid",
              borderColor: palettePrimaryContrast,
              ":hover": {
                backgroundColor: `color-mix(in srgb, ${palettePrimaryContrast} 10%, transparent)`,
              },
              ":active": {
                backgroundColor: `color-mix(in srgb, ${palettePrimaryContrast} 18%, transparent)`,
                transform: "translateY(0.5px)",
              },
              "&.Mui-disabled, &:disabled": {
                color: `color-mix(in srgb, ${palettePrimaryContrast} 38%, transparent)`,
                borderColor: `color-mix(in srgb, ${palettePrimaryContrast} 24%, transparent)`,
                backgroundColor: "transparent",
              },
              "&:focus-visible": {
                outline: "none",
                boxShadow: `0 0 0 3px color-mix(in srgb, ${palettePrimaryContrast} 35%, transparent)`,
              },
            },
          },
          {
            props: { variant: "outlinedContrast", size: "small" },
            style: {
              fontSize: "14px",
              padding: "0 20px",
              height: "30px",
            },
          },
        ],
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            backgroundColor: "white",
            "& .MuiFormHelperText-root": {
              backgroundColor: backgroundMain,
              margin: 0, 
              padding: 0
            },
          },
        },
      },
      MuiPickersTextField: {
        styleOverrides: {
          root: {
            backgroundColor: "white",
            "& .MuiFormHelperText-root": {
              backgroundColor: backgroundMain,
              margin: 0,
              padding: 0,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: primaryContrast === "#000000" ? "#000000" : primaryMain, // Changes label color when focused
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: primaryContrast === "#000000" ? "#000000" : primaryMain, // Changes border on focus
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              color: primaryContrast === "#000000" ? "#000000" : primaryMain,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: primaryContrast === "#000000" ? "#000000" : primaryMain, // Change indicator (underline) color
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            "&.Mui-checked": {
              color: primaryContrast === "#000000" ? "#000000" : primaryMain,
            },
          },
        },
      },
      MuiCard: {
        variants: [
          {
            props: { variant: "Card1" },
            style: {
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "#E0E3E7",
              borderRadius: "10px",
              backgroundColor: "white",
              boxShadow: "0px 4px 8px -3px rgba(0, 0, 0, 0.25)",
            },
          },
        ],
      },
    },
    palette: {
      primary: {
        main: palettePrimaryMain,
        contrastText: palettePrimaryContrast,
        hover: primaryHover,
        opposite: primaryOpposite,
        oppositeHover: primaryOppositeHover,
        green: primaryGreen,
      },
      secondary: {
        main: secondaryMain,
        hover: secondaryHover,
      },
      tertiary: {
        main: tertiaryMain,
        contrastText: "#0d1220",
      },
      background: {
        main: backgroundMain,
        hover: backgroundHover,
      },
      accent1: {
        main: accent1,
      },
      accent2: {
        main: accent2,
      },
      sheets: {
        main: "#188038",
        hover: "rgba(24, 128, 56, 0.5)",
      },
    },
  });
};

export default useCustomTheme;
